<template>
  <div class="case-preview-v2">
    <div v-if="loading" class="d-flex justify-content-center align-items-center vh-100 bg-white">
      <v-loader />
    </div>

    <template v-else-if="caseData">
      <!-- 1. HEADER (Sticky & Print Header) -->
      <div class="preview-header-v1 bg-black text-white hide-print">
        <div class="container py-40">
          <div class="row align-items-center">
            <div class="col-md-8">
              <h2 class="h5 text-uppercase letter-spacing-2 opacity-70 mb-12">
                {{ $t('travelBook.headerTile') }}
              </h2>
              <h1 class="h3 fw-bold mb-16">{{ caseData.name }}</h1>
              <div v-if="caseData.tour_name && caseData.tour_name !== caseData.name" class="h6 opacity-70 mb-16 text-uppercase letter-spacing-1">{{ caseData.tour_name }}</div>

              <div class="d-flex flex-wrap align-items-center gap-16 text-sm">
                <div class="d-flex align-items-center">
                  <span class="mi mi-location me-8 opacity-60" />
                  <span>{{ caseData.origin?.name }}</span>
                  <span class="mi mi-arrow-chevron-forward mx-8 opacity-40 text-xs" />
                  <span>{{ caseData.destination?.name }}</span>
                </div>
                <div class="v-divider"></div>
                <div class="d-flex align-items-center">
                  <span class="mi mi-calandar me-8 opacity-60" />
                  <span>{{ formatDate(caseData.start_date) }} - {{ formatDate(caseData.end_date_formatted) }}</span>
                </div>
              </div>

              <div class="d-flex flex-wrap align-items-center gap-24 mt-16 text-xs text-uppercase fw-bold letter-spacing-1">
                <div class="d-flex align-items-center">
                  <span class="mi mi-three-people me-8 opacity-60" />
                  {{ caseData.paxes?.length || 0 }} {{ $t('general.shared.paxes') }}
                </div>
                <div class="d-flex align-items-center">
                  <span class="mi mi-profile me-8 opacity-60" />
                  {{ getTravelTypeLabel(caseData.travel_type) }}
                </div>
              </div>
            </div>
            <div class="col-md-4 text-center text-md-end mt-32 mt-md-0">
              <img src="https://site.waw.travel/images/wawlogo.svg" alt="WAW" height="50" class="invert-filter mb-20" />
              <div>
                <v-button variant="outline-light" size="sm" class="rounded-0 text-uppercase x-small fw-bold px-24" @click="printPage">
                  <span class="mi mi-printer me-8" /> Print Travel Book
                </v-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Layout Table (for PDF/Print consistency) -->
      <main class="bg-white">
        <div class="container py-32 py-md-64">
          <div class="row justify-content-center">
            <div class="col-lg-10 col-xl-9">

              <!-- 1. CONTACT RESUME (First Section) -->
              <section class="mb-64 mb-md-80">
                <h3 class="section-title-v1">{{ $t('travelBook.contact') }}</h3>
                <div class="card rounded-0 border p-24 p-md-32 bg-light-soft">
                  <div class="row align-items-center">
                    <div class="col-md-8">
                      <div class="fw-bold text-uppercase small letter-spacing-1 mb-8">Prepared For</div>
                      <h4 class="h5 mb-4">{{ caseData.client?.name }} {{ caseData.client?.surname }}</h4>
                      <div class="text-muted small mb-0">{{ caseData.client?.email }}</div>
                    </div>
                    <div class="col-md-4 text-md-end mt-24 mt-md-0">
                      <div class="text-muted x-small text-uppercase letter-spacing-1 mb-4">Reference</div>
                      <div class="fw-bold">#{{ caseData.number }}</div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- 2. FEATURED IMAGE -->
              <section v-if="caseData.activity?.featuredImage" class="mb-64 mb-md-80 featured-img-v1">
                <img :src="caseData.activity.featuredImage.original_url || caseData.activity.featuredImage.storageUrl"
                     class="img-fluid w-100 shadow-sm border grayscale-hover"
                     alt="Trip Featured Image" />
              </section>

              <!-- 3. TOUR RESUME (Table of Contents with Before/After) -->
              <section class="mb-64 mb-md-80 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.tourResume') }}</h3>
                <div class="toc-list">
                  <!-- Before Your Trip -->
                  <div
                    v-if="caseData.before_trip_information?.description"
                    class="toc-item d-flex justify-content-between align-items-center py-16 px-20 border-bottom cursor-pointer"
                    @click="if(!isTocSectionOpen('before')) toggleTocSection('before'); scrollToSection('before')"
                  >
                    <div class="d-flex align-items-center gap-16">
                      <div class="fw-bold x-small text-uppercase letter-spacing-2 text-muted">Before</div>
                      <div class="fw-semi text-black small">Before Your Trip</div>
                    </div>
                    <div class="mi" :class="isTocSectionOpen('before') ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                  </div>

                  <!-- Daily Itinerary -->
                  <div
                    v-for="(day, index) in caseData.caseDays"
                    :key="day.id"
                    class="toc-item d-flex justify-content-between align-items-center py-16 px-20 border-bottom cursor-pointer"
                    @click="if(!isDayOpen(day.id)) toggleDay(day.id); scrollToDay(day.id)"
                  >
                    <div class="d-flex align-items-center gap-16 flex-grow-1">
                      <div class="day-label fw-bold x-small text-uppercase letter-spacing-2 text-muted">
                        Day {{ (index + 1) < 10 ? '0' + (index + 1) : (index + 1) }}
                      </div>
                      <div class="day-route fw-semi text-black small">
                        {{ day.origin?.name }}
                        <span v-if="day.destination" class="mx-8 opacity-30 fw-light">→</span>
                        <span v-if="day.destination">{{ day.destination.name }}</span>
                        <span v-if="day.stage_length || day.stageLength" class="ms-8 text-muted fw-normal x-small">
                          ({{ day.stage_length || day.stageLength }} km)
                        </span>
                      </div>
                    </div>
                    <div class="ms-auto mi mi-arrow-chevron-forward opacity-30"></div>
                  </div>

                  <!-- After Your Trip -->
                  <div
                    v-if="caseData.after_trip_information?.description"
                    class="toc-item d-flex justify-content-between align-items-center py-16 px-20 border-bottom cursor-pointer"
                    @click="if(!isTocSectionOpen('after')) toggleTocSection('after'); scrollToSection('after')"
                  >
                    <div class="d-flex align-items-center gap-16">
                      <div class="fw-bold x-small text-uppercase letter-spacing-2 text-muted">After</div>
                      <div class="fw-semi text-black small">Post Journey Info</div>
                    </div>
                    <div class="mi" :class="isTocSectionOpen('after') ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                  </div>
                </div>
              </section>

              <!-- 4. BEFORE YOUR TRIP (Expandable Accordion) -->
              <section v-if="caseData.before_trip_information?.description" id="section-before" class="mb-64 mb-md-80 break-inside-avoid">
                <div class="accordion-section border">
                  <div
                    class="accordion-header p-20 p-md-24 bg-light-soft cursor-pointer d-flex justify-content-between align-items-center"
                    :class="{ 'border-bottom-0': !isTocSectionOpen('before') }"
                    @click="toggleTocSection('before')"
                  >
                    <h3 class="small fw-bold mb-0 text-uppercase letter-spacing-1">Before Your Trip</h3>
                    <div class="mi" :class="isTocSectionOpen('before') ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                  </div>
                  <div v-if="isTocSectionOpen('before')" class="accordion-body p-24 p-md-40 bg-white">
                    <div class="prose text-dark mb-24" v-html="getBeforeTripPreview()"></div>
                    <v-button
                      v-if="caseData.before_trip_information.description.length > 400"
                      variant="link"
                      size="sm"
                      class="p-0 text-black fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                      @click="toggleBeforeTripExpanded"
                    >
                      {{ isBeforeTripExpanded ? 'View Less' : 'View More' }}
                    </v-button>
                  </div>
                </div>
              </section>

              <!-- 5. JOURNEY MAP -->
              <section v-if="itineraryLocations.length" class="mb-64 mb-md-80 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.map') }}</h3>
                <div class="map-container-v3 border shadow-sm grayscale-hover" style="height: 450px;">
                  <v-map :items="itineraryLocations" :zoom="6" :draw-path="true" />
                </div>
              </section>

              <!-- 6. DETAILED ITINERARY -->
               <section class="mb-64 mb-md-80">
                 <h3 class="section-title-v1">{{ $t('travelBook.tour') }}</h3>

                 <div v-for="(day, index) in caseData.caseDays" :key="day.id" :id="`day-${day.id}`" class="day-accordion mb-24 border">
                  <!-- Day Header (Accordion Toggle) -->
                  <div
                    class="day-accordion-header d-flex justify-content-between align-items-center p-20 p-md-24 cursor-pointer bg-light-soft"
                    :class="{ 'border-bottom-0': !isDayOpen(day.id) }"
                    @click="toggleDay(day.id)"
                  >                    <div class="d-flex align-items-center gap-16 gap-md-24 flex-grow-1">
                      <div class="text-uppercase x-small fw-bold letter-spacing-2 text-muted">Day {{ (index + 1) < 10 ? '0' + (index + 1) : (index + 1) }}</div>
                      <h4 class="h6 fw-bold mb-0">
                        {{ day.origin?.name }}
                        <span v-if="day.destination" class="mx-8 opacity-30 fw-light">→</span>
                        <span v-if="day.destination">{{ day.destination.name }}</span>
                      </h4>
                      <div v-if="day.stageLength" class="ms-auto stage-length x-small text-muted me-16">
                        {{ day.stageLength }} km
                      </div>
                    </div>
                    <div class="d-flex align-items-center gap-16">
                      <div class="text-end d-none d-sm-block">
                        <div class="fw-bold x-small text-uppercase">{{ formatDate(day.date_formatted, 'EEEE') }}</div>
                      </div>
                      <span class="mi" :class="isDayOpen(day.id) ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></span>
                    </div>
                  </div>

                  <!-- Accordion Body -->
                  <div v-if="isDayOpen(day.id)" class="day-accordion-body p-24 p-md-40 bg-white">
                    <!-- Day Items (Services) - Grouped -->
                    <div class="day-services">
                      <template v-for="group in getGroupedDayItems(day.caseDayItems)" :key="`${group.type}-${group.id}`">
                        <div class="service-item mb-40 mb-md-64 break-inside-avoid">
                          <div class="row g-24 g-md-40">
                            <!-- Image Carousel (Shared for the group) -->
                            <div v-if="getItemImages(group.items[0]).length > 0" class="col-md-6 mb-24 mb-md-0">
                              <div class="image-carousel-wrapper">
                                <div class="carousel-container border grayscale-hover position-relative" :style="{ aspectRatio: '16/10' }">
                                  <img
                                    :src="getItemImages(group.items[0])[carouselIndex(group.id)]"
                                    class="img-fluid w-100 h-100 object-fit-cover"
                                    :alt="getItemTitle(group.items[0])"
                                  />
                                  <!-- Carousel Controls -->
                                  <div v-if="getItemImages(group.items[0]).length > 1" class="carousel-controls position-absolute bottom-0 end-0">
                                    <button
                                      class="carousel-btn carousel-prev"
                                      @click.stop="prevCarouselImage(group.id)"
                                    >
                                      <span class="mi mi-arrow-chevron-back"></span>
                                    </button>
                                    <span class="carousel-counter">{{ carouselIndex(group.id) + 1 }}/{{ getItemImages(group.items[0]).length }}</span>
                                    <button
                                      class="carousel-btn carousel-next"
                                      @click.stop="nextCarouselImage(group.id)"
                                    >
                                      <span class="mi mi-arrow-chevron-forward"></span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div :class="getItemImages(group.items[0]).length > 0 ? 'col-md-6' : 'col-12'">
                              <div class="d-flex align-items-center gap-12 mb-12">
                                <div class="service-type-badge text-uppercase x-small fw-bold bg-black text-white px-8 py-2">
                                  {{ group.type.replace('_', ' ') }}
                                </div>
                                <span v-if="group.items.length > 1" class="badge border border-dark text-black x-small rounded-0 px-8 text-uppercase">
                                  {{ group.items.length }} {{ group.items.length === 1 ? 'Service' : 'Services' }}
                                </span>
                              </div>

                              <h5 class="h6 fw-bold mb-16 text-black">
                                <span v-if="group.items.length > 1" class="text-muted fw-normal me-8">{{ group.items.length }}x</span>
                                {{ getItemTitle(group.items[0]) }}
                              </h5>

                              <!-- Shared Item Description (only from first item in group) -->
                              <div v-if="group.items[0].item_description" class="service-desc-wrapper mb-24">
                                <div
                                  class="service-desc prose small mb-12 text-muted"
                                  v-html="
                                    isExpanded(group.id) || group.items[0].item_description.length <= 400
                                      ? group.items[0].item_description
                                      : truncateDescription(group.items[0].item_description)
                                  "
                                ></div>

                                <v-button
                                  v-if="group.items[0].item_description.length > 400"
                                  variant="link"
                                  size="sm"
                                  class="p-0 text-black fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                                  @click="toggleExpand(group.id)"
                                >
                                  {{ isExpanded(group.id) ? 'View Less' : 'View More' }}
                                </v-button>
                              </div>

                              <!-- Consolidated Resource Details -->
                              <div v-if="group.groupedResources?.length" class="service-resources">
                                <div v-for="res in group.groupedResources" :key="res.id" class="resource-row p-16 border bg-light-soft mb-12">
                                  <div class="d-flex justify-content-between align-items-center mb-12">
                                    <div class="fw-bold small text-uppercase letter-spacing-1">
                                      <span v-if="res.count > 1" class="text-muted fw-normal me-8">{{ res.count }}x</span>
                                      {{ res.resource_name }}
                                    </div>
                                    <span v-if="res.nights > 0" class="badge border border-dark text-black x-small rounded-0 px-8">{{ res.nights }} nights</span>
                                  </div>
                                  <div class="d-flex flex-wrap gap-12 gap-md-16 x-small text-muted mb-16">
                                    <span v-if="res.checkin_date"><b>Check-in:</b> {{ formatDate(res.checkin_date, 'dd MMM') }}</span>
                                    <span v-if="res.checkout_date"><b>Check-out:</b> {{ formatDate(res.checkout_date, 'dd MMM') }}</span>
                                    <span v-if="res.origin_location?.name"><b>From:</b> {{ res.origin_location.name }}</span>
                                    <span v-if="res.destination_location?.name"><b>To:</b> {{ res.destination_location.name }}</span>
                                  </div>

                                  <!-- Amenities -->
                                  <div v-if="res.amenities?.length" class="d-flex flex-wrap gap-8 mb-16">
                                    <span
                                      v-for="amenity in res.amenities"
                                      :key="amenity"
                                      class="x-small border bg-white rounded-pill px-12 py-4 text-muted text-uppercase fw-bold letter-spacing-1"
                                      style="font-size: 10px;"
                                    >
                                      {{ amenity.replace('_', ' ') }}
                                    </span>
                                  </div>

                                  <div v-if="res.supplier_info" class="supplier-links d-flex gap-16 border-top pt-16">
                                    <a
                                      v-if="res.supplier_info.map_url"
                                      :href="res.supplier_info.map_url"
                                      target="_blank"
                                      class="x-small text-black fw-bold text-decoration-none d-flex align-items-center gap-4"
                                    >
                                      <span class="mi mi-map text-xs" /> Map
                                    </a>
                                    <a
                                      v-if="res.supplier_info.phone"
                                      :href="`tel:${res.supplier_info.phone}`"
                                      class="x-small text-black fw-bold text-decoration-none d-flex align-items-center gap-4"
                                    >
                                      <span class="mi mi-call text-xs" /> {{ res.supplier_info.phone }}
                                    </a>
                                    <a
                                      v-if="res.supplier_info.site_url"
                                      :href="res.supplier_info.site_url"
                                      target="_blank"
                                      class="x-small text-black fw-bold text-decoration-none d-flex align-items-center gap-4"
                                    >
                                      <span class="mi mi-world-net text-xs" /> Website
                                    </a>
                                  </div>
                                </div>
                              </div>
                              <!-- Grouped Notes -->
                              <div v-if="group.allNotes?.length" class="service-notes-container mt-20">
                                <div v-for="(note, nIdx) in group.allNotes" :key="nIdx" class="service-notes border-start border-3 ps-20 py-4 mb-12 small text-muted italic" v-html="note"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>

                      <!-- Location Guide Information (What to Do) - Expandable -->
                      <div v-for="locInfo in getDayLocationInfo(day)" :key="locInfo.id" class="location-info-block mt-32 border break-inside-avoid">
                        <div
                          class="location-guide-header p-20 p-md-24 bg-light-soft cursor-pointer d-flex justify-content-between align-items-center"
                          :class="{ 'border-bottom-0': !isLocationGuideOpen(locInfo.id) }"
                          @click="toggleLocationGuide(locInfo.id)"
                        >
                          <div class="d-flex align-items-center gap-12">
                            <span class="mi mi-camera opacity-40 text-xs" />
                            <h5 class="x-small mb-0 fw-bold text-uppercase letter-spacing-1">{{ $t('travelBook.whatToDoLocation', { location: locInfo.locationName }) }}</h5>
                          </div>
                          <div class="mi" :class="isLocationGuideOpen(locInfo.id) ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                        </div>

                        <div v-if="isLocationGuideOpen(locInfo.id)" class="p-24 p-md-40 bg-white">
                          <div class="prose small text-muted mb-24" v-html="getLocationGuidePreview(locInfo)"></div>

                          <v-button
                            v-if="locInfo.description && locInfo.description.length > 400"
                            variant="link"
                            size="sm"
                            class="p-0 text-black fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                            @click="toggleLocationGuideExpand(locInfo.id)"
                          >
                            {{ isLocationGuideExpanded(locInfo.id) ? 'View Less' : 'View More' }}
                          </v-button>

                          <!-- Location Media Carousel -->
                          <div v-if="locInfo.media?.length" class="mt-24">
                            <div class="location-media-carousel position-relative" style="aspect-ratio: 16/10; max-height: 300px;">
                              <img
                                :src="locInfo.media[locationCarouselIndex(locInfo.id)]?.original_url || locInfo.media[locationCarouselIndex(locInfo.id)]?.storageUrl"
                                class="img-fluid w-100 h-100 object-fit-cover border grayscale-hover"
                              />
                              <!-- Media Controls -->
                              <div v-if="locInfo.media.length > 1" class="carousel-controls position-absolute bottom-0 end-0">
                                <button
                                  class="carousel-btn carousel-prev"
                                  @click.stop="prevLocationCarouselImage(locInfo.id)"
                                >
                                  <span class="mi mi-arrow-chevron-back"></span>
                                </button>
                                <span class="carousel-counter">{{ locationCarouselIndex(locInfo.id) + 1 }}/{{ locInfo.media.length }}</span>
                                <button
                                  class="carousel-btn carousel-next"
                                  @click.stop="nextLocationCarouselImage(locInfo.id)"
                                >
                                  <span class="mi mi-arrow-chevron-forward"></span>
                                </button>
                              </div>
                            </div>
                          </div>

                          <div class="mt-24 d-flex gap-16">
                            <a v-if="locInfo.mapUrl" :href="locInfo.mapUrl" target="_blank" class="x-small text-black fw-bold text-decoration-none d-flex align-items-center gap-4">
                              <span class="mi mi-location text-xs" /> {{ $t('travelBook.viewGoogleMaps') }}
                            </a>
                            <a v-if="locInfo.siteUrl" :href="locInfo.siteUrl" target="_blank" class="x-small text-black fw-bold text-decoration-none d-flex align-items-center gap-4">
                              <span class="mi mi-world-net text-xs" /> {{ $t('travelBook.viewWebsite') }}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- 7. AFTER YOUR TRIP (Expandable Accordion) -->
              <section v-if="caseData.after_trip_information?.description" id="section-after" class="mb-64 mb-md-80 break-inside-avoid">
                <div class="accordion-section border">
                  <div
                    class="accordion-header p-20 p-md-24 bg-light-soft cursor-pointer d-flex justify-content-between align-items-center"
                    :class="{ 'border-bottom-0': !isTocSectionOpen('after') }"
                    @click="toggleTocSection('after')"
                  >
                    <h3 class="small fw-bold mb-0 text-uppercase letter-spacing-1">Post Journey Info</h3>
                    <div class="mi" :class="isTocSectionOpen('after') ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                  </div>
                  <div v-if="isTocSectionOpen('after')" class="accordion-body p-24 p-md-40 bg-white">
                    <div class="prose text-dark mb-24" v-html="getAfterTripPreview()"></div>
                    <v-button
                      v-if="caseData.after_trip_information.description.length > 400"
                      variant="link"
                      size="sm"
                      class="p-0 text-black fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                      @click="toggleAfterTripExpanded"
                    >
                      {{ isAfterTripExpanded ? 'View Less' : 'View More' }}
                    </v-button>
                  </div>
                </div>
              </section>

              <!-- 8. EMERGENCY CONTACTS -->
              <section v-if="caseData.contact_text" class="mb-64 mb-md-80 break-inside-avoid">
                <div class="accordion-section border">
                  <div
                    class="accordion-header p-20 p-md-24 bg-light-soft cursor-pointer d-flex justify-content-between align-items-center"
                    :class="{ 'border-bottom-0': !isTocSectionOpen('support') }"
                    @click="toggleTocSection('support')"
                  >
                    <h3 class="small fw-bold mb-0 text-uppercase letter-spacing-1">Support & Contacts</h3>
                    <div class="mi" :class="isTocSectionOpen('support') ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                  </div>
                  <div v-if="isTocSectionOpen('support')" class="accordion-body p-24 p-md-40 bg-white">
                    <div class="prose small text-muted mb-24" v-html="getSupportPreview()"></div>
                    <v-button
                      v-if="caseData.contact_text.length > 400"
                      variant="link"
                      size="sm"
                      class="p-0 text-black fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                      @click="toggleSupportExpanded"
                    >
                      {{ isSupportExpanded ? 'View Less' : 'View More' }}
                    </v-button>
                  </div>
                </div>
              </section>

              <!-- 9. INCLUSIONS & EXCLUSIONS -->
              <section v-if="hasInclusionsInfo" class="mb-64 mb-md-80 break-inside-avoid">
                <div class="accordion-section border">
                  <div
                    class="accordion-header p-20 p-md-24 bg-light-soft cursor-pointer d-flex justify-content-between align-items-center"
                    :class="{ 'border-bottom-0': !isTocSectionOpen('inclusions') }"
                    @click="toggleTocSection('inclusions')"
                  >
                    <h3 class="small fw-bold mb-0 text-uppercase letter-spacing-1">What's Included & Not Included</h3>
                    <div class="mi" :class="isTocSectionOpen('inclusions') ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                  </div>
                  <div v-if="isTocSectionOpen('inclusions')" class="accordion-body p-24 p-md-40 bg-white">
                    <div class="row g-32 g-md-48">
                      <div v-if="caseData.included_trip_information?.description" class="col-lg-6 mb-32 mb-lg-0">
                        <h4 class="fw-bold x-small text-uppercase letter-spacing-1 mb-20">What's Included</h4>
                        <div class="prose small text-muted border-start ps-24" v-html="caseData.included_trip_information.description"></div>
                      </div>
                      <div v-if="caseData.excluded_trip_information?.description" class="col-lg-6">
                        <h4 class="fw-bold x-small text-uppercase letter-spacing-1 mb-20">What's Not Included</h4>
                        <div class="prose small text-muted border-start ps-24" v-html="caseData.excluded_trip_information.description"></div>
                      </div>
                      <div v-if="caseData.optional_trip_information?.description" class="col-12 mt-32 mt-md-48">
                        <h4 class="fw-bold x-small text-uppercase letter-spacing-1 mb-20">Optional Extras</h4>
                        <div class="prose small text-muted border-start ps-24" v-html="caseData.optional_trip_information.description"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- 10. CTA -->
              <section v-if="caseData.is_draft" class="mb-64 mb-md-80 break-inside-avoid">
                <div class="cta-banner-v1 bg-black text-white p-32 p-md-64 text-center">
                  <h2 class="h2 fw-extra-bold mb-20 text-white text-uppercase letter-spacing-2">Ready to Adventure?</h2>
                  <p class="opacity-70 mb-32 mb-md-48 lead">Finalize your reservation and lock in your personalized itinerary today.</p>
                  <div class="d-flex flex-wrap justify-content-center gap-16 gap-md-24">
                    <v-button variant="light" size="lg" class="rounded-0 fw-bold px-32 py-12 px-md-40 py-md-16 text-uppercase x-small letter-spacing-2 shadow-sm" @click="contactWhatsApp">
                      WhatsApp Specialist
                    </v-button>
                    <v-button variant="outline-light" size="lg" class="rounded-0 fw-bold px-32 py-12 px-md-40 py-md-16 text-uppercase x-small letter-spacing-2" @click="bookNow">
                      Request Reservation
                    </v-button>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </main>

      <!-- 11. FOOTER -->
      <footer class="footer-v1 bg-black text-white py-48">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-md-8 text-center text-md-start mb-24 mb-md-0">
              <div class="d-flex justify-content-center justify-content-md-start gap-24 mb-16 text-uppercase text-sm letter-spacing-3 fw-bold opacity-50">
                <a href="#" class="text-white text-decoration-none">Instagram</a>
                <a href="#" class="text-white text-decoration-none">Facebook</a>
                <a href="#" class="text-white text-decoration-none">LinkedIn</a>
              </div>
              <p class="text-xs opacity-30 mb-0">&copy; {{ new Date().getFullYear() }} WAW Travels. All rights reserved.</p>
              <p class="text-xs fw-bold letter-spacing-2 mt-8 opacity-50">SITE.WAW.TRAVEL</p>
            </div>
            <div class="col-md-4 text-center text-md-end">
              <img src="https://site.waw.travel/images/wawlogo.svg" alt="WAW" class="invert-filter" style="height: 24px; width: auto;" />
            </div>
          </div>
        </div>
      </footer>

      <!-- Back to Top -->
      <div v-if="showScrollTop" class="back-to-top-v1 hide-print shadow-lg" @click="scrollToTop">
        <span class="mi mi-arrow-chevron-up" />
      </div>
    </template>

    <div v-else class="vh-100 d-flex flex-column align-items-center justify-content-center bg-white text-center p-32">
      <img src="https://site.waw.travel/images/wawlogo.svg" alt="WAW" height="50" class="mb-48 opacity-10" />
      <h2 class="fw-bold text-uppercase letter-spacing-3 small text-muted mb-16">Itinerary Not Found</h2>
      <p class="text-muted max-w-400 mb-32 small">We couldn't retrieve this itinerary. It may have expired or the link is invalid.</p>
      <v-button variant="dark" class="rounded-0 px-32 py-12 text-uppercase x-small fw-bold letter-spacing-2" @click="$router.push('/')">
        Return Home
      </v-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRoute } from "vue-router";
import api from "@/api";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VMap from "@/components/vendor/VMap.vue";
import { DateTime } from "luxon";
import { findConceptByKey } from "@/helpers/ConceptHelper";

const route = useRoute();
const loading = ref(true);
const caseData = ref<any>(null);
const showScrollTop = ref(false);
const openDays = ref<number[]>([]);
const expandedItems = ref<number[]>([]);
const openTocSections = ref<string[]>([]);
const openLocationGuides = ref<number[]>([]);
const expandedLocationGuides = ref<number[]>([]);
const isBeforeTripExpanded = ref(false);
const isAfterTripExpanded = ref(false);
const isSupportExpanded = ref(false);
const carouselIndices = ref<Record<number, number>>({});
const locationCarouselIndices = ref<Record<number, number>>({});

const toggleDay = (dayId: number) => {
  const index = openDays.value.indexOf(dayId);
  if (index > -1) openDays.value.splice(index, 1);
  else openDays.value.push(dayId);
};

const isDayOpen = (dayId: number) => openDays.value.includes(dayId);

const toggleExpand = (itemId: number) => {
  const index = expandedItems.value.indexOf(itemId);
  if (index > -1) expandedItems.value.splice(index, 1);
  else expandedItems.value.push(itemId);
};

const isExpanded = (itemId: number) => expandedItems.value.includes(itemId);

const scrollToDay = (dayId: number) => {
  setTimeout(() => {
    const el = document.getElementById(`day-${dayId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 100);
};

const scrollToSection = (sectionId: string) => {
  setTimeout(() => {
    const el = document.getElementById(`section-${sectionId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 100);
};

const truncateDescription = (description: string, limit = 400) => {
  if (!description) return "";
  if (description.length <= limit) return description;

  // Basic truncate that tries not to break HTML tags too badly
  const truncated = description.substring(0, limit);
  return truncated + "...";
};

const formatDate = (date: string, format = "dd LLL yyyy") => {
  if (!date) return "";
  return DateTime.fromISO(date).toFormat(format);
};

const getTravelTypeLabel = (typeKey: string) => {
  if (!typeKey) return "";
  const concept = findConceptByKey('tour.travel_type', typeKey);
  return concept?.name || typeKey.replace('_', ' ').toUpperCase();
};

const normalizeCaseData = (data: any) => {
  if (!data) return null;
  const normalized = { ...data };

  // Map snake_case to camelCase for the template
  if (data.case_days && !data.caseDays) normalized.caseDays = data.case_days;
  if (data.pax_count && (!data.paxes || data.paxes.length === 0)) {
    normalized.paxes = Array(data.pax_count).fill({});
  }

  if (normalized.caseDays) {
    normalized.caseDays = normalized.caseDays.map((day: any) => {
      const nDay = { ...day };
      if (day.case_day_items && !day.caseDayItems) nDay.caseDayItems = day.case_day_items;
      if (day.stage_length && !day.stageLength) nDay.stageLength = day.stage_length;
      if (nDay.caseDayItems) {
        nDay.caseDayItems = nDay.caseDayItems.map((item: any) => {
          const nItem = { ...item };
          if (item.item_data && !item.itemData) nItem.itemData = item.item_data;

          if (item.case_day_item_resources && !item.caseDayItemResources) {
            nItem.caseDayItemResources = item.case_day_item_resources.map((res: any) => {
              const nRes = { ...res };
              const sRes = res.supplier_resource || res.supplierResource;
              if (sRes) {
                nRes.supplierResource = sRes;
                nRes.resource_name = sRes.name;
                nRes.supplier_info = sRes.supplier;
                if (sRes.amenities) {
                  try {
                    nRes.amenities = typeof sRes.amenities === 'string' ? JSON.parse(sRes.amenities) : sRes.amenities;
                  } catch (e) {
                    nRes.amenities = [];
                  }
                }
              }
              return nRes;
            });
          }
          return nItem;
        });

        // Try to find stageLength if missing from day object
        if (!nDay.stageLength) {
          const stageInfo = nDay.caseDayItems.find((i: any) => i.type === 'stage_information');
          if (stageInfo?.itemData?.stage_length) {
            nDay.stageLength = stageInfo.itemData.stage_length;
          }
        }
      }
      return nDay;
    });
  }

  // Handle translations for before/after trip info based on client preference
  const lang = data.client?.preferred_language || 'en';
  if (normalized.before_trip_information?.translations) {
    const translation = normalized.before_trip_information.translations.find((t: any) => t.locale === lang);
    if (translation) normalized.before_trip_information.description = translation.description;
  }
  if (normalized.after_trip_information?.translations) {
    const translation = normalized.after_trip_information.translations.find((t: any) => t.locale === lang);
    if (translation) normalized.after_trip_information.description = translation.description;
  }

  return normalized;
};

const getItemTitle = (item: any) => {
  const data = item.itemData || item.item_data;
  if (item.type === "stage_information" || item.type === "general_information") {
    return data?.title || item.type.replace("_", " ").toUpperCase();
  }
  return data?.name || item.type.replace("_", " ").toUpperCase();
};

const getDayLocationInfo = (day: any) => {
  const infos: any[] = [];
  // Origin locations
  if (day.origin?.resources?.length) {
    day.origin.resources.filter((r: any) => r.type === 'information').forEach((r: any) => {
      infos.push({ ...r, locationName: day.origin.name });
    });
  }
  // Destination locations
  if (day.destination?.resources?.length) {
    day.destination.resources.filter((r: any) => r.type === 'information').forEach((r: any) => {
      infos.push({ ...r, locationName: day.destination.name });
    });
  }
  return infos;
};

// Service Grouping - Group items by type and ID to avoid repetition
const getGroupedDayItems = (items: any[]) => {
  const groups: Record<string, any> = {};
  items.forEach(item => {
    const data = item.itemData || item.item_data;
    // Only group if it's a "service" product with an ID.
    // Types like 'general_information' or 'stage_information' stay unique.
    const isGroupable = item.type !== 'general_information' &&
                       item.type !== 'stage_information' &&
                       data?.id;

    const groupKey = isGroupable ? `${item.type}-${data.id}` : `unique-${item.id}`;

    if (!groups[groupKey]) {
      groups[groupKey] = {
        type: item.type,
        items: [],
        itemData: data,
        id: item.id // Use first item id as base for carousel/expand state
      };
    }
    groups[groupKey].items.push(item);
  });

  return Object.values(groups).map((group: any) => {
    // Consolidation: Group identical resources within this service group
    const resources: any[] = [];
    group.items.forEach((item: any) => {
      if (item.caseDayItemResources) {
        resources.push(...item.caseDayItemResources);
      }
    });

    const groupedResources: Record<string, any> = {};
    resources.forEach(res => {
      // Create a key based on name and nights/dates to group identical rooms/items
      const resKey = `${res.resource_name}-${res.nights}-${res.checkin_date}-${res.checkout_date}`;
      if (!groupedResources[resKey]) {
        groupedResources[resKey] = { ...res, count: 1 };
      } else {
        groupedResources[resKey].count++;
      }
    });
    group.groupedResources = Object.values(groupedResources);

    // Collect all unique notes
    const notesSet = new Set();
    group.items.forEach((i: any) => {
      if (i.notes) notesSet.add(i.notes);
    });
    group.allNotes = Array.from(notesSet);

    return group;
  });
};

// Get all images from an item
const getItemImages = (item: any) => {
  const data = item.itemData || item.item_data;
  if (data?.media && data.media.length > 0) {
    return data.media.map((m: any) => m.original_url || m.preview_url || m.storageUrl || m.storage_url);
  }
  return [];
};

// Carousel management for services
const carouselIndex = (itemId: number) => carouselIndices.value[itemId] || 0;
const nextCarouselImage = (itemId: number) => {
  const item = caseData.value?.caseDays?.flatMap((d: any) => d.caseDayItems).find((i: any) => i.id === itemId);
  if (item) {
    const images = getItemImages(item);
    const current = carouselIndex(itemId);
    carouselIndices.value[itemId] = (current + 1) % images.length;
  }
};
const prevCarouselImage = (itemId: number) => {
  const item = caseData.value?.caseDays?.flatMap((d: any) => d.caseDayItems).find((i: any) => i.id === itemId);
  if (item) {
    const images = getItemImages(item);
    const current = carouselIndex(itemId);
    carouselIndices.value[itemId] = (current - 1 + images.length) % images.length;
  }
};

// Carousel management for location guides
const locationCarouselIndex = (locId: number) => locationCarouselIndices.value[locId] || 0;
const nextLocationCarouselImage = (locId: number) => {
  const day = caseData.value?.caseDays?.find((d: any) =>
    getDayLocationInfo(d).some((l: any) => l.id === locId)
  );
  if (day) {
    const locInfo = getDayLocationInfo(day).find((l: any) => l.id === locId);
    if (locInfo?.media) {
      const current = locationCarouselIndex(locId);
      locationCarouselIndices.value[locId] = (current + 1) % locInfo.media.length;
    }
  }
};
const prevLocationCarouselImage = (locId: number) => {
  const day = caseData.value?.caseDays?.find((d: any) =>
    getDayLocationInfo(d).some((l: any) => l.id === locId)
  );
  if (day) {
    const locInfo = getDayLocationInfo(day).find((l: any) => l.id === locId);
    if (locInfo?.media) {
      const current = locationCarouselIndex(locId);
      locationCarouselIndices.value[locId] = (current - 1 + locInfo.media.length) % locInfo.media.length;
    }
  }
};

// Location Guide expand/collapse
const toggleLocationGuide = (locId: number) => {
  const index = openLocationGuides.value.indexOf(locId);
  if (index > -1) openLocationGuides.value.splice(index, 1);
  else openLocationGuides.value.push(locId);
};
const isLocationGuideOpen = (locId: number) => openLocationGuides.value.includes(locId);

const toggleLocationGuideExpand = (locId: number) => {
  const index = expandedLocationGuides.value.indexOf(locId);
  if (index > -1) expandedLocationGuides.value.splice(index, 1);
  else expandedLocationGuides.value.push(locId);
};
const isLocationGuideExpanded = (locId: number) => expandedLocationGuides.value.includes(locId);

const getLocationGuidePreview = (locInfo: any) => {
  if (!locInfo.description) return "";
  const isExpanded = isLocationGuideExpanded(locInfo.id);
  if (isExpanded || locInfo.description.length <= 400) return locInfo.description;
  return truncateDescription(locInfo.description);
};

// Tour Resume Sections (Before/After)
const toggleTocSection = (section: string) => {
  const index = openTocSections.value.indexOf(section);
  if (index > -1) openTocSections.value.splice(index, 1);
  else openTocSections.value.push(section);
};
const isTocSectionOpen = (section: string) => openTocSections.value.includes(section);

const getBeforeTripPreview = () => {
  if (!caseData.value?.before_trip_information?.description) return "";
  const desc = caseData.value.before_trip_information.description;
  if (isBeforeTripExpanded.value || desc.length <= 400) return desc;
  return truncateDescription(desc);
};

const getAfterTripPreview = () => {
  if (!caseData.value?.after_trip_information?.description) return "";
  const desc = caseData.value.after_trip_information.description;
  if (isAfterTripExpanded.value || desc.length <= 400) return desc;
  return truncateDescription(desc);
};

const getSupportPreview = () => {
  if (!caseData.value?.contact_text) return "";
  const desc = caseData.value.contact_text;
  if (isSupportExpanded.value || desc.length <= 400) return desc;
  return truncateDescription(desc);
};

const toggleBeforeTripExpanded = () => {
  isBeforeTripExpanded.value = !isBeforeTripExpanded.value;
};

const toggleAfterTripExpanded = () => {
  isAfterTripExpanded.value = !isAfterTripExpanded.value;
};

const toggleSupportExpanded = () => {
  isSupportExpanded.value = !isSupportExpanded.value;
};

const itineraryLocations = computed(() => {
  if (!caseData.value?.caseDays) return [];

  const locations: any[] = [];
  const addedIds = new Set();

  caseData.value.caseDays.forEach((day: any) => {
    if (day.origin && !addedIds.has(day.origin.id)) {
      locations.push({
        id: day.origin.id,
        name: day.origin.name,
        lat: parseFloat(day.origin.lat),
        lng: parseFloat(day.origin.lng)
      });
      addedIds.add(day.origin.id);
    }
    if (day.destination && !addedIds.has(day.destination.id)) {
      locations.push({
        id: day.destination.id,
        name: day.destination.name,
        lat: parseFloat(day.destination.lat),
        lng: parseFloat(day.destination.lng)
      });
      addedIds.add(day.destination.id);
    }
  });

  return locations;
});

const hasInclusionsInfo = computed(() => {
  return caseData.value?.included_trip_information?.description ||
         caseData.value?.excluded_trip_information?.description ||
         caseData.value?.optional_trip_information?.description;
});

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleScroll = () => {
  showScrollTop.value = window.scrollY > 500;
};

const printPage = () => {
  window.print();
};

const contactWhatsApp = () => {
  const caseName = caseData.value.name;
  const caseNumber = caseData.value.number;
  const msg = encodeURIComponent(`Hi! I'm interested in the trip: ${caseName} (Ref: #${caseNumber})`);
  window.open(`https://wa.me/34600000000?text=${msg}`, '_blank');
};

const bookNow = () => {
  const subject = encodeURIComponent(`Booking Request: ${caseData.value.name} (#${caseData.value.number})`);
  const body = encodeURIComponent(`Hi WAW Travels team,\n\nI want to book: ${window.location.href}\n\nThanks!`);
  window.location.href = `mailto:hello@waw.travel?subject=${subject}&body=${body}`;
};

onMounted(async () => {
  window.addEventListener('scroll', handleScroll);
  const caseId = route.params.caseId as string;

  if (!caseId || caseId === 'mock') {
    loading.value = false;
    return;
  }

  try {
    const response = await api.case.casePreview(caseId);
    caseData.value = normalizeCaseData(response.content);
  } catch (error) {
    console.error("Failed to fetch preview data:", error);
  } finally {
    loading.value = false;
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

.case-preview-v2 {
  background-color: #fdfdfd;
  min-height: 100vh;
  font-family: 'Poppins', sans-serif;
  color: #000000;
  -webkit-font-smoothing: antialiased;
}

/* Utilities */
.fw-extra-bold { font-weight: 800; }
.letter-spacing-1 { letter-spacing: 1px; }
.letter-spacing-2 { letter-spacing: 2px; }
.letter-spacing-3 { letter-spacing: 4px; }
.x-small { font-size: 0.65rem; }
.max-w-400 { max-width: 400px; }
.invert-filter { filter: brightness(0) invert(1); }
.grayscale-hover img {
  filter: grayscale(1);
  transition: filter 0.5s ease;
  &:hover { filter: grayscale(0); }
}
.bg-light-soft { background-color: #fafafa; }
.v-divider { width: 1px; height: 24px; background-color: rgba(255,255,255,0.2); }

/* Header V1 */
.preview-header-v1 {
  background-color: #000;
  border-bottom: 1px solid #333;
}

/* Hero V3 */
.hero-section {
  height: 60vh;
  min-height: 500px;
  background-color: #000;
}
.hero-image-wrapper { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
.hero-img { width: 100%; height: 100%; object-fit: cover; filter: brightness(0.7) contrast(1.1); }
.hero-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: linear-gradient(0deg, rgba(0,0,0,0.8) 0%, transparent 100%); }

.hero-title { font-size: clamp(2.5rem, 6vw, 4rem); line-height: 1.1; text-transform: uppercase; }

/* Sections */
.section-title-v1 {
  font-weight: 800;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: #999;
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;
  margin-bottom: 40px;
}

.section-heading-minimal { border-bottom: none; display: inline-block; }

.overview-content { font-size: 1.25rem; line-height: 1.8; font-weight: 300; }

/* Map */
.map-container-v3 {
  width: 100%;
  background-color: #f5f5f5;
  border: 1px solid #eee;
  :deep(.map) { height: 100%; }
}

/* Timeline Minimal */
.day-number { line-height: 1; min-width: 80px; }
.service-img-wrap { aspect-ratio: 16/10; overflow: hidden; background-color: #f5f5f5; }
.resource-row {
  border-left: 3px solid #000 !important;
  transition: all 0.2s ease;
  &:hover { background-color: #f0f0f0 !important; }
}

/* Carousel Styles */
.carousel-container {
  position: relative;
  overflow: hidden;
  background-color: #f5f5f5;
}

.carousel-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}

.carousel-btn {
  background-color: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.2);
    color: #fff;
  }
}

.carousel-counter {
  color: #fff;
  font-size: 0.7rem;
  text-align: center;
  min-width: 45px;
  font-weight: 600;
  letter-spacing: 1px;
}

/* TOC List Styles */
.toc-list {
  border: 1px solid #eee;

  .toc-item {
    transition: all 0.2s ease;
    border-bottom-color: #eee !important;

    &:hover {
      background-color: #f8f8f8;
      padding-left: 24px !important;
    }
  }
}

.stage-length {
  margin-left: auto;
  padding-right: 16px;
}

/* Accordion Styles */
.day-accordion, .accordion-section {
  border: 1px solid #eee;
  border-radius: 0;
}

.accordion-section {
  .accordion-header {
    background-color: #fafafa;
    border-bottom: 1px solid #eee;
    transition: all 0.2s ease;

    &:hover {
      background-color: #f5f5f5;
    }

    h3 {
      font-size: 0.85rem;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
  }

  .accordion-body {
    background-color: #fff;
  }
}

.day-accordion-header {
  border-bottom: 1px solid #eee;
  transition: all 0.2s ease;
  &:hover {
    background-color: #f5f5f5;
  }
}

.location-guide-header {
  background-color: #fafafa;
  border-bottom: 1px solid #eee;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }
}

/* Day Divider */
.day-divider-v1 {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin: 40px 0;
  .dot { width: 6px; height: 6px; border-radius: 50%; background-color: #ccc; }
}

/* Location Guide */
.location-info-block {
  border: 1px solid #eee;
  border-left: 4px solid #000 !important;
  margin-top: 48px !important;
}

/* CTA */
.cta-banner-v1 {
  box-shadow: 0 30px 60px rgba(0,0,0,0.1);
  border: 1px solid #eee;
}

/* Back to Top */
.back-to-top-v1 {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 45px;
  height: 45px;
  background-color: #000;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1040;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    background-color: #333;
  }
}

/* Print */
@media print {
  .hide-print { display: none !important; }
  .preview-header-v1 { display: none !important; }
  .hero-section { height: 400px !important; background: white !important; }
  .hero-title, .hero-meta, .hero-label { color: black !important; text-shadow: none !important; }
  .hero-overlay { display: none; }
  .day-detail-block { break-inside: avoid; }
  .bg-black { background-color: #000 !important; color: #fff !important; print-color-adjust: exact; }
  .bg-light-soft { background-color: #f8f9fa !important; print-color-adjust: exact; }
  .table-v1 thead { background-color: #000 !important; color: #fff !important; print-color-adjust: exact; }
  .day-divider-v1 { display: none; }
  section { margin-bottom: 40px !important; }
}

/* Responsiveness */
@media (max-width: 991.98px) {
  .hero-section { height: 50vh; min-height: 400px; }
  .hero-title { font-size: 2.5rem; }
  .day-number { font-size: 3rem; min-width: 60px; }
  .section-title-v1 { margin-bottom: 32px; }
}

@media (max-width: 575.98px) {
  .hero-meta { gap: 16px; flex-direction: column; align-items: center; }
  .day-header { flex-direction: column; align-items: flex-start !important; gap: 12px; }
  .day-number { font-size: 2rem; }
  .back-to-top-v1 { bottom: 20px; right: 20px; }
}
</style>
